<template>
  <label class="option-input" :disabled="isDisabled">
    <input
      v-if="!hideInput"
      :type="type"
      class="option-input__input"
      :value="value"
      :checked="isChecked"
      :disabled="isDisabled"
      @change="updateFn"
    />

    <span
      class="option-input__label-wrapper"
      :class="{
        'option-input__label-wrapper--has-image': icon,
        'option-input__label-wrapper--checkbox': type === 'checkbox',
      }"
    >
      <img v-if="icon" :src="icon" alt="" class="option-input__icon" />

      <slot></slot>

      <span v-if="option.hint" class="option-input__hint">
        {{ option.hint }}
      </span>
    </span>
  </label>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { getImage } from '@/models/onboarding'
import type { QuestionnaireFormOption } from '@/models/onboarding.types'

const props = withDefaults(
  defineProps<{
    value: string
    updateFn: (event: Event) => void
    modelValue: string[]
    option: QuestionnaireFormOption
    maxOptions?: number
    type?: 'radio' | 'checkbox'
    hideInput?: boolean
  }>(),
  {
    type: 'radio',
    hideInput: false,
  },
)

const isChecked = computed(() => {
  return props.modelValue.includes(props.value)
})

const isDisabled = computed(() => {
  if (!props.maxOptions) {
    return false
  }

  return (
    props.type === 'checkbox' &&
    props.modelValue?.length >= props.maxOptions &&
    isChecked.value === false
  )
})

const icon = computed(() => {
  if (!props.option.icon) {
    return
  }
  return getImage(props.option.icon)
})
</script>

<style lang="scss" scoped>
.option-input {
  width: 100%;
  display: flex;

  &[disabled='true'] {
    opacity: 0.6;
    pointer-events: none;
  }

  &__label-wrapper {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 19px;
    border: 1px solid $c-border-2;
    background: $c-white;
    border-radius: 14px;
    font-size: 16px;
    line-height: 24px;
    letter-spacing: -0.02em;
    color: $c-black-15;
    transition: all 0.15s ease-in-out;

    &:has(input:focus) {
      background: $c-yellow-2;
      border-color: $c-yellow;
    }

    @include hover {
      cursor: pointer;
      border-color: $c-black-3;
    }

    &:active {
      background: $c-bg;
      border-color: $c-black-3;
    }
  }

  &__label-wrapper--has-image {
    padding-block: 15px;
  }

  &__label-wrapper--checkbox {
    &:after {
      content: '';
      display: flex;
      justify-content: center;
      flex-shrink: 0;
      width: 20px;
      height: 20px;
      border: 1px solid $c-black-6;
      border-radius: 4px;
      margin-left: auto;
      background: transparent;
      transition: all 0.15s ease-in-out;
    }
  }

  &__label {
    width: 100%;
  }

  &__label--text-input {
    font-size: inherit;
    font-weight: inherit;
    letter-spacing: inherit;
    color: inherit;
    background: transparent;
  }

  &__input {
    position: absolute;
    z-index: -1;
    appearance: none;
    background: transparent;
    margin: 0;

    &:checked + .option-input__label-wrapper {
      background: $c-yellow-2;
      border-color: $c-yellow;
    }

    &:checked + .option-input__label-wrapper--checkbox {
      &:after {
        content: url('@/assets.app/images/onboarding/check.svg');
        background: $c-yellow;
        border-color: $c-yellow;
      }
    }
  }

  &__icon {
    width: 32px;
    aspect-ratio: 1/1;
  }

  &__hint {
    margin-left: auto;
    font-size: 14px;
    line-height: 1;
    color: $c-black-6;
  }
}
</style>
